import { useState } from "react";
import {
	ChevronDown,
	ChevronRight,
	ClipboardCopy,
	CornerDownLeft,
	Link2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { HintTooltip } from "./HintTooltip";
import { exerciseTypeIcon } from "./exerciseIcons";
import {
	EXERCISE_TYPES,
	formatExerciseDisplayName,
	formatExercisePrescription,
	formatExerciseSetRowLines,
	getNormalizedDayExercises,
} from "../../lib/programBuilderUtils";
import { setExerciseDragPayload } from "../../lib/programClipboard";
import { formatDate } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Read-only, one-line-per-exercise rendering of a week.
 *
 * This is a *reference* view, not a second editor: it summarises rather than
 * reproducing the editor's inline forms, so a whole week stays scannable at
 * ~28px per exercise and a coach can compare progression vertically.
 */

const exerciseKey = (dayIndex, exerciseIndex) => `${dayIndex}:${exerciseIndex}`;

// La vista e' usata anche fuori dal pannello di riferimento (la sola lettura di
// un programma condiviso), dove non c'e' nessuna selezione da propagare: senza
// un default `selectedKeys.has()` fa saltare l'intera pagina.
const NO_SELECTION = new Set();

const isSharedReadonly = (exercise) =>
	exercise?.shared_readonly === true && exercise?.shared_is_origin !== true;

function ReferenceExerciseRow({
	exercise,
	dayIndex,
	exerciseIndex,
	selectionMode,
	isSelected,
	onToggleSelect,
	onCopy,
}) {
	const { t } = useLanguage();
	const [expanded, setExpanded] = useState(false);

	const exerciseType = exercise?.exercise_type || "strength";
	const TypeIcon = exerciseTypeIcon(exerciseType);
	const typeColor =
		EXERCISE_TYPES.find((item) => item.value === exerciseType)?.color ||
		"text-volt";
	const name = formatExerciseDisplayName(exercise);
	const prescription = formatExercisePrescription(exercise, t);
	const setRowLines = formatExerciseSetRowLines(exercise);
	const notes = String(exercise?.notes || "").trim();
	const readonly = isSharedReadonly(exercise);
	// Expandable whenever there is anything more to read. The prescription counts:
	// conditioning rows are the longest and the most truncated, so they were
	// precisely the ones a click had to open.
	const hasDetail =
		Boolean(prescription) || setRowLines.length > 1 || Boolean(notes);

	return (
		<div className="border-b border-border/40 last:border-b-0">
			<div
				// Draggable straight onto a day of the editor. Shared read-only
				// exercises cannot be copied, so they cannot be dragged either.
				draggable={!selectionMode && !readonly}
				onDragStart={(event) => {
					if (selectionMode || readonly) return;
					setExerciseDragPayload(event.dataTransfer, [{ ...exercise }]);
				}}
				className={`group flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/40 ${
					!selectionMode && !readonly
						? "cursor-grab active:cursor-grabbing"
						: ""
				}`}
			>
				{selectionMode ? (
					<Checkbox
						checked={isSelected}
						disabled={readonly}
						onCheckedChange={() => onToggleSelect?.(dayIndex, exerciseIndex)}
						aria-label={name}
						className="shrink-0"
					/>
				) : (
					<TypeIcon className={`w-3.5 h-3.5 shrink-0 ${typeColor}`} />
				)}

				<button
					type="button"
					onClick={() => {
						// In selection mode the whole row is the checkbox target: hunting
						// for a 16px box while multi-selecting would be needless precision.
						if (selectionMode) {
							if (!readonly) onToggleSelect?.(dayIndex, exerciseIndex);
							return;
						}
						if (hasDetail) setExpanded((prev) => !prev);
					}}
					className="flex-1 min-w-0 flex items-baseline gap-2 text-left"
					aria-expanded={!selectionMode && hasDetail ? expanded : undefined}
				>
					{/* The name takes the remaining space; the prescription is capped so a
					    long conditioning line truncates instead of overflowing the panel
					    and squeezing the name down to a single letter. */}
					<span className="flex-1 min-w-0 text-xs text-foreground truncate">
						{name}
					</span>
					{prescription && (
						<span className="shrink-0 max-w-[48%] font-mono text-xs md:text-[11px] text-muted-foreground truncate">
							{prescription}
						</span>
					)}
				</button>

				{readonly && (
					<HintTooltip label={t("pbReferenceSharedReadonly")}>
						<span className="shrink-0 inline-flex">
							<Link2 className="w-3 h-3 text-amber-400/80" />
						</span>
					</HintTooltip>
				)}

				{!selectionMode &&
					hasDetail &&
					(expanded ? (
						<ChevronDown className="w-3 h-3 shrink-0 text-muted-foreground" />
					) : (
						<ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />
					))}

				{!selectionMode && !readonly && onCopy && (
					<HintTooltip label={t("pbReferenceCopyExercise")}>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={() => onCopy(dayIndex, exerciseIndex)}
							aria-label={t("pbReferenceCopyExercise")}
							className="h-8 w-8 md:h-6 md:w-6 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100 focus:opacity-100 hover:text-volt"
						>
							<ClipboardCopy className="w-3.5 h-3.5" />
						</Button>
					</HintTooltip>
				)}
			</div>

			{expanded && !selectionMode && (
				<div className="px-2 pb-2 pl-7 space-y-1">
					{prescription && (
						<p className="font-mono text-xs md:text-[11px] text-foreground/90 whitespace-pre-wrap break-words">
							{prescription}
						</p>
					)}
					{setRowLines.length > 1 &&
						setRowLines.map((line, index) => (
							<p
								key={index}
								className="font-mono text-xs md:text-[11px] text-muted-foreground"
							>
								{line}
							</p>
						))}
					{notes && (
						<p className="text-xs md:text-[11px] text-muted-foreground/80 italic whitespace-pre-wrap">
							{notes}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

export function ReferenceWeekView({
	week,
	selectionMode,
	selectedKeys = NO_SELECTION,
	onToggleSelect,
	onCopyExercise,
	onCopyDay,
	onJumpToDay,
}) {
	const { t } = useLanguage();
	const days = week?.days || [];

	if (days.length === 0) {
		return (
			<p className="px-3 py-6 text-xs text-muted-foreground text-center">
				{t("pbReferenceEmptyWeek")}
			</p>
		);
	}

	return (
		<div className="divide-y divide-border">
			{days.map((day, dayIndex) => {
				const exercises = getNormalizedDayExercises(day);
				const dayLabel = String(day?.title || "").trim() || `${dayIndex + 1}`;
				const dateLabel = day?.date ? formatDate(day.date) : "";
				// Senza handler non c'e' niente da copiare: la vista di sola lettura
				// non monta la clipboard dell'editor.
				const copyable =
					Boolean(onCopyDay) &&
					exercises.some((exercise) => !isSharedReadonly(exercise));

				return (
					<div key={day?.day_id || dayIndex}>
						<div className="flex items-center gap-2 px-2 py-1.5 bg-secondary/30 sticky top-0 z-10">
							<span className="text-[11px] md:text-[10px] uppercase tracking-widest text-muted-foreground truncate">
								{dayLabel}
								{dateLabel ? ` · ${dateLabel}` : ""}
							</span>
							<div className="ml-auto shrink-0 flex items-center gap-0.5">
								{onJumpToDay && !selectionMode && (
									<HintTooltip label={t("pbReferenceJumpToDay")}>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() => onJumpToDay(dayIndex)}
											aria-label={t("pbReferenceJumpToDay")}
											className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-volt"
										>
											<CornerDownLeft className="w-3.5 h-3.5" />
										</Button>
									</HintTooltip>
								)}
								{copyable && !selectionMode && (
									<div className="shrink-0">
										<HintTooltip label={t("pbReferenceCopyDay")}>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => onCopyDay(dayIndex)}
												aria-label={t("pbReferenceCopyDay")}
												className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-volt"
											>
												<ClipboardCopy className="w-3.5 h-3.5" />
											</Button>
										</HintTooltip>
									</div>
								)}
							</div>
						</div>

						{exercises.length === 0 ? (
							<p className="px-2 py-2 text-xs md:text-[11px] text-muted-foreground/70">
								{t("pbReferenceEmptyDay")}
							</p>
						) : (
							exercises.map((exercise, exerciseIndex) => (
								<ReferenceExerciseRow
									key={exercise?.exercise_id || exerciseIndex}
									exercise={exercise}
									dayIndex={dayIndex}
									exerciseIndex={exerciseIndex}
									selectionMode={selectionMode}
									isSelected={selectedKeys.has(
										exerciseKey(dayIndex, exerciseIndex),
									)}
									onToggleSelect={onToggleSelect}
									onCopy={onCopyExercise}
								/>
							))
						)}
					</div>
				);
			})}
		</div>
	);
}

export { exerciseKey, isSharedReadonly };
